import React, {
  useEffect,
  useMemo,
} from "react";
import { ok } from "neverthrow";
import {
  FormItemComponent,
  FormItemProps,
} from "../../../FormItem";
import {
  useSingleFormItem,
  UseSingleFormItemProps,
} from "../../../useSingleFormItem";
import StandardInputContainer from "../../../StandardInputContainer";
import SuggestionsWrapper from "../../../SuggestionsWrapper";
import TextInput from "../../../TextInput";
import { FormItemProviderProps } from "../../../FormItemProvider";
import { FieldType } from "../../../../../../domain/reports/FieldTypes";
import Suggestion from "../../../Suggestion";

type GenericTextOptions = {
  textarea?: boolean,
  capitalize?: boolean,
  disabled?: boolean,
};

type GenericTextFormItemProps<O> =
  & FormItemProps<O>
  & UseSingleFormItemProps<string, O>
  & {
    fieldName: string,
  }
  & GenericTextOptions;

function GenericTextFormItem<O>(props: GenericTextFormItemProps<O>): FormItemComponent<O> {
  const hook = useSingleFormItem<string, O>(props);

  return (
    <StandardInputContainer
      required={props.required}
      isOk={hook.isOk}
      isError={hook.isError}
      errors={hook.error}
      fieldName={props.fieldName}
    >
      <SuggestionsWrapper<O>
        pickSuggestion={hook.pickSuggestion}
        showSuggestions={hook.showSuggestions}
        suggestions={props.suggestions}
      >
        <TextInput
          onChange={hook.onInputChange}
          isError={hook.isError}
          value={hook.input}
          placeholder={props.fieldName}
          capitalize={props.capitalize}
          textarea={props.textarea}
          disabled={props.disabled}
        />
      </SuggestionsWrapper>
    </StandardInputContainer>
  );
}

type GenericTextFormItemProviderProps<O> =
  & FormItemProviderProps<O>
  & UseSingleFormItemProps<string, O>
  & GenericTextOptions
  & {
    suggestPrevious?: boolean,
    autoFillPrevious?: boolean,
    fieldName: string,
    fieldType: FieldType,
  }
  & (
    {
      suggestPrevious: true,
      outputToSuggestionText: (output: O) => string
    } |
    {
      suggestPrevious?: false,
      outputToSuggestionText?: (output: O) => string
    }
  );

function GenericTextFormItemProvider<O>(props: GenericTextFormItemProviderProps<O>): React.ReactElement {
  const suggestions: Suggestion<O>[] = useMemo(() => {
    if (!props.suggestPrevious) {
      return [];
    }
    const potentialPrevValue = props.previousReport?.data?.[props.fieldType];
    if (potentialPrevValue === undefined || potentialPrevValue === null) {
      return [];
    }
    const prevValue: O = potentialPrevValue as unknown as O;
    return [{
      text: props.outputToSuggestionText(prevValue),
      value: prevValue,
    }];
  }, [props.previousReport]);

  useEffect(() => {
    if (!props.autoFillPrevious) {
      return;
    }
    const potentialPrevValue = props.previousReport?.data?.[props.fieldType];
    if (
      potentialPrevValue === undefined ||
      potentialPrevValue === null ||
      (props.result?.isOk() && props.result.value !== undefined)
    ) {
      return;
    }
    const prevValue: O = potentialPrevValue as unknown as O;
    props.onResultChange(ok(prevValue));
  }, [props.previousReport]);

  return (
    <GenericTextFormItem
      suggestions={suggestions}
      {...props}
    />
  );
}

export default GenericTextFormItemProvider;